// Libs
import classNames from 'classnames/bind';
import { memo, useEffect, useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  UniqueIdentifier,
  closestCorners,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useTranslation } from 'react-i18next';
import { SortableContext, arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { Box, Typography } from '@mui/material';
// Components, Layouts, Pages
import Column from './ColumnKanban';
import Items from './ItemKanban';
// Others
import { DEFAULT_OPACITY_HEX_COLOR_KANBAN, EMPTY_STRING } from '~/utils/constants/common';
import { ColumnTypeKanban, IBaseKanban, IStyleColumnKanban, ItemChangeKanban } from '~/utils/interface/common';
// Styles, images, icons
import styles from './BaseKanban.module.scss';

export type BaseKanbanProps<T extends IBaseKanban> = {
  data: ColumnTypeKanban<T>[];
  disabled?: boolean;
  renderItem: (item: T) => React.ReactNode;
  onItemChange: (data: ItemChangeKanban<T>, item?: T, newStatus?: string) => void;
  styleColumn: (id: string) => IStyleColumnKanban;
};

const cx = classNames.bind(styles);

function BaseKanban<T extends IBaseKanban>(props: BaseKanbanProps<T>) {
  //#region Destructuring Props
  const { data, disabled, renderItem, onItemChange, styleColumn } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/pipeline']);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [columns, setColumns] = useState<ColumnTypeKanban<T>[]>([]);
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    if (data) {
      setColumns(
        data.map((column) => ({
          ...column,
          items: column.items.map((item) => ({
            ...item,
            itemId: item.uuid || (Math.random().toString() as UniqueIdentifier),
            uuid: `item-${item.id || Math.random().toString()}`,
          })),
          columnId: column.uuid,
          uuid: `column-${column.id || Math.random().toString()}`,
        }))
      );
    }
  }, [data]);
  //#endregion Implement Hook

  //#region Handle Function
  function findValueOfItems(uuid: UniqueIdentifier | undefined, type: string) {
    if (type === 'column') return columns.find((item) => item.uuid === uuid);
    if (type === 'item') return columns.find((column) => column.items.find((item: T) => item.uuid === uuid));
  }

  const findItem = (uuid: UniqueIdentifier | undefined) => {
    const column = findValueOfItems(uuid, 'item');
    if (!column) return null;
    const item = column.items.find((item: T) => item.uuid === uuid);
    if (!item) return null;

    return item;
  };

  function handleDragStart(event: DragStartEvent) {
    const { active } = event;
    const { id } = active;
    setActiveId(id);
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (
      active.id.toString().includes('item') &&
      over?.id.toString().includes('item') &&
      active &&
      over &&
      active.id !== over.id
    ) {
      const activeContainer = findValueOfItems(active.id, 'item');
      const overContainer = findValueOfItems(over.id, 'item');

      if (!activeContainer || !overContainer) return;

      // Find the index of the active and over column
      const activeColumnIndex = columns.findIndex((column) => column.uuid === activeContainer.uuid);
      const overColumnIndex = columns.findIndex((column) => column.uuid === overContainer.uuid);

      // Find the index of the active and over item
      const activeItemIndex = activeContainer.items.findIndex((item: T) => item.uuid === active.id);
      const overItemIndex = overContainer.items.findIndex((item: T) => item.uuid === over.id);

      // In the same column
      let newItems = [...columns];
      if (activeColumnIndex === overColumnIndex) {
        newItems[activeColumnIndex].items = arrayMove(
          newItems[activeColumnIndex].items,
          activeItemIndex,
          overItemIndex
        );

        setColumns(newItems);
      } else {
        // In different column
        const [removeditem] = newItems[activeColumnIndex].items.splice(activeItemIndex, 1);
        newItems[overColumnIndex].items.splice(overItemIndex, 0, removeditem);
        setColumns(newItems);
      }

      if (onItemChange) {
        const movedItem = findItem(activeId || EMPTY_STRING);
        const newStatus = overContainer?.name || '';

        onItemChange(
          {
            columnId: String(overContainer?.id),
            columnPosition: String(overColumnIndex + 1),
            itemId: String(activeId).replace('item-', ''),
            itemPosition: String(overItemIndex + 1),
            data: newItems,
          },
          movedItem as T,
          newStatus
        );
      }
    }

    // Handling item dropping into Column
    if (
      active.id.toString().includes('item') &&
      over?.id.toString().includes('column') &&
      active &&
      over &&
      active.id !== over.id
    ) {
      // Find the active and over column
      const activeContainer = findValueOfItems(active.id, 'item');
      const overContainer = findValueOfItems(over.id, 'column');

      if (!activeContainer || !overContainer) return;

      // Find the index of the active and over column
      const activeColumnIndex = columns.findIndex((column) => column.uuid === activeContainer.uuid);
      const overColumnIndex = columns.findIndex((column) => column.uuid === overContainer.uuid);

      // Find the index of the active and over item
      const activeItemIndex = activeContainer.items.findIndex((item: T) => item.uuid === active.id);

      let newItems = [...columns];
      const [removeditem] = newItems[activeColumnIndex].items.splice(activeItemIndex, 1);
      newItems[overColumnIndex].items.push(removeditem);
      setColumns(newItems);

      if (onItemChange) {
        const movedItem = findItem(activeId || EMPTY_STRING);
        const newStatus = overContainer?.name || EMPTY_STRING;

        onItemChange(
          {
            columnId: String(overContainer?.id),
            columnPosition: String(overColumnIndex + 1),
            itemId: String(activeId).replace('item-', ''),
            itemPosition: String(newItems[overColumnIndex].items.length),
            data: newItems,
          },
          movedItem as T,
          newStatus
        );
      }
    }

    setActiveId(null);
  }

  const renderNameColumn = (column: ColumnTypeKanban<T>) => {
    const style = styleColumn(column.id);

    const styleTotal = {
      backgroundColor: `${style?.color}${DEFAULT_OPACITY_HEX_COLOR_KANBAN}`,
      color: style?.color,
    };

    return (
      <Box className={cx('columnName')} style={style}>
        <Typography className={cx('name')}>{column.name}</Typography>

        <Typography className={cx('total')} style={styleTotal}>
          {column.items.length}
        </Typography>
      </Box>
    );
  };
  //#endregion Handle Function

  return (
    <Box id='baseKanbanComponent' className={cx('container')}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={columns.map((i) => i.uuid)}>
          <Box className={cx('columns')}>
            {columns.map((column: ColumnTypeKanban<T>) => (
              <Box
                component={'div'}
                className={cx('columnContainer')}
                key={column.uuid}
                style={{ borderColor: styleColumn(column.id)?.borderColor }}
              >
                <Column id={column.uuid} render={renderNameColumn} data={column}>
                  <SortableContext items={column.items.map((i: any) => i.uuid)}>
                    <Box className={cx('contentColumn')}>
                      <Box className={cx('column')}>
                        {column.items.map((item: any) => (
                          <Items
                            data={item}
                            columnData={column}
                            id={item.uuid}
                            key={item.uuid}
                            render={renderItem}
                            disabled={disabled}
                          />
                        ))}
                      </Box>
                    </Box>
                  </SortableContext>
                </Column>
              </Box>
            ))}
          </Box>
        </SortableContext>
        <DragOverlay adjustScale={false}>
          {activeId && activeId.toString().includes('item') && (
            <Items id={activeId} data={findItem(activeId)} render={renderItem} />
          )}
        </DragOverlay>
      </DndContext>
    </Box>
  );
}

export default memo(BaseKanban) as typeof BaseKanban;
